'use client';

import { useLanguage } from '@/context/LanguageContext';
import { Search, Layers, Monitor, Globe } from 'lucide-react';
import Reveal, { StaggerChildren, StaggerItem } from './Reveal';

const steps = [
  {
    icon: Search,
    fr: {
      title: 'Audit & Diagnostic',
      desc: "Nous analysons votre environnement IT et votre présence digitale pour identifier les failles, les risques et les opportunités.",
    },
    en: {
      title: 'Audit & Diagnostic',
      desc: 'We analyze your IT environment and digital presence to identify gaps, risks, and opportunities.',
    },
  },
  {
    icon: Layers,
    fr: {
      title: 'Infrastructure & Sécurité',
      desc: "Nous concevons et déployons une infrastructure solide : réseau, sécurité, cloud et continuité d\u2019activité.",
    },
    en: {
      title: 'Infrastructure & Security',
      desc: 'We design and deploy a solid foundation: network, security, cloud, and business continuity.',
    },
  },
  {
    icon: Monitor,
    fr: {
      title: 'Supervision & Support',
      desc: "Nous surveillons vos systèmes en continu et intervenons avant que les problèmes n\u2019impactent votre activité.",
    },
    en: {
      title: 'Supervision & Support',
      desc: 'We continuously monitor your systems and intervene before issues impact your operations.',
    },
  },
  {
    icon: Globe,
    fr: {
      title: 'Présence Digitale & Croissance',
      desc: "Nous construisons votre vitrine en ligne (site web, portails clients, outils connectés) pour que votre image reflète votre ambition.",
    },
    en: {
      title: 'Digital Presence & Growth',
      desc: 'We build your online storefront (website, client portals, connected tools) so your image matches your ambition.',
    },
  },
];

export default function HowWeHelp() {
  const { lang, t } = useLanguage();

  return (
    <section id="how-we-help" className="relative py-20 md:py-28 bg-dark-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-sm font-semibold text-electric tracking-widest uppercase mb-3">
              {t('Notre approche', 'Our approach')}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              {t('Comment nous vous accompagnons', 'How we support you')}
            </h2>
            <p className="mt-4 text-lg text-white/50">
              {t(
                "De l\u2019audit initial à votre présence en ligne, une méthodologie complète pour tout votre numérique.",
                'From the initial audit to your online presence, a complete methodology for your entire digital world.'
              )}
            </p>
          </div>
        </Reveal>

        <div className="relative">
          {/* Connecting gradient line */}
          <div
            className="hidden lg:block absolute top-8 left-[12%] right-[12%] h-px"
            aria-hidden="true"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(79,142,255,0.3), rgba(34,211,238,0.3), transparent)',
            }}
          />

          <StaggerChildren className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
            {steps.map((step, i) => {
              const Icon = step.icon;
              const content = lang === 'fr' ? step.fr : step.en;
              return (
                <StaggerItem key={i}>
                  <div className="relative text-center group">
                    <div className="w-16 h-16 rounded-full bg-electric/10 border border-electric/20 text-electric flex items-center justify-center mx-auto mb-5 relative z-10 group-hover:bg-electric/20 group-hover:border-electric/40 group-hover:shadow-[0_0_25px_rgba(79,142,255,0.2)] transition-all duration-500">
                      <Icon className="w-7 h-7" />
                    </div>
                    <p className="text-xs font-bold text-electric/60 uppercase tracking-widest mb-2">
                      {t(`Étape ${i + 1}`, `Step ${i + 1}`)}
                    </p>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {content.title}
                    </h3>
                    <p className="text-white/55 leading-relaxed text-sm">
                      {content.desc}
                    </p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerChildren>
        </div>
      </div>
    </section>
  );
}
